import { useTranslation } from "react-i18next";

import type { ButtonProps } from "features/components/Button";
import Button from "features/components/Button";
import Dialog from "features/components/Dialog";
import WarningIcon from "features/ui/Icons/WarningIcon";

interface Props {
  title?: string;
  text?: string | React.ReactNode;
  isOpen: boolean;
  onClose: (confirm: boolean) => void;
  loading?: boolean;
  hideCTA?: boolean;
  closeDisabled?: boolean;
  confirmDisabled?: boolean;
  confirmText?: string;
  closeText?: string;
  confirmColor?: ButtonProps["color"];
  additionalAction?: React.ReactNode;
  error?: string;
}

const ConfirmationModal = ({
  title,
  text = "",
  isOpen,
  onClose,
  loading = false,
  hideCTA = false,
  closeDisabled = false,
  confirmDisabled = false,
  confirmText,
  closeText,
  confirmColor = "primary",
  additionalAction = null,
  error,
}: Props) => {
  const { t } = useTranslation();
  const isDestructive = confirmColor === "error";

  const cancelButton = (
    <Button
      color={isDestructive || hideCTA ? "primary" : "secondary"}
      onClick={() => onClose(false)}
      testId="button-confirmation-cancel"
      disabled={closeDisabled}
      size="small"
      autoFocus
    >
      {closeText ?? (isDestructive ? t("common:cancel") : t("common:close"))}
    </Button>
  );

  const confirmButton = !hideCTA && (
    <Button
      color={confirmColor}
      size="small"
      disabled={loading || confirmDisabled}
      loading={loading}
      onClick={() => onClose(true)}
      testId="button-confirmation-confirm"
    >
      {confirmText ?? t("common:confirm")}
    </Button>
  );

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="xs"
      onClose={() => {
        // Escape and the backdrop reach here too, so a dialog that cannot be cancelled cannot be dismissed either.
        if (!closeDisabled) onClose(false);
      }}
      title={title ?? t("common:confirmation.title")}
      actions={
        <>
          {additionalAction}
          {isDestructive ? confirmButton : cancelButton}
          {isDestructive ? cancelButton : confirmButton}
        </>
      }
    >
      {error && (
        <div className="mt-1 flex items-center text-sm gap-2 text-gray-500">
          <WarningIcon className="inline align-bottom text-red-400 mr-1" />
          <div>{error}</div>
        </div>
      )}
      {text}
    </Dialog>
  );
};

export default ConfirmationModal;
